package com.wintermar.inspection.feature.domain.usecase.inspection

import com.wintermar.inspection.core.data.Uuid
import com.wintermar.inspection.core.domain.FlowResult
import com.wintermar.inspection.core.domain.mapResultResourceAsFlow
import com.wintermar.inspection.core.ui.component.datetime.util.DateTimeUtil
import com.wintermar.inspection.feature.domain.model.answer.AnswerModel
import com.wintermar.inspection.feature.domain.model.inspection.InspectionModel
import com.wintermar.inspection.feature.domain.model.template.InspectionTemplateModel
import com.wintermar.inspection.feature.domain.repository.InspectionRepository
import com.wintermar.inspection.feature.domain.repository.TemplateRepository
import com.wintermar.inspection.feature.domain.repository.answer.AnswerRepository
import com.wintermar.inspection.graphql.type.InspectionStatus
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOn

class CreateNewInspectionUseCase(
    private val templateRepository: TemplateRepository,
    private val inspectionRepository: InspectionRepository,
    private val answerRepository: AnswerRepository
) {
    private fun insertInspection(
        inspectionId: String,
        conductedIso: String,
        templateData: InspectionTemplateModel?,
    ): Flow<FlowResult<InspectionModel>> {
        val data = InspectionModel(
            id = inspectionId,
            templateId = templateData?.id ?: "",
            templateName = templateData?.name ?: "",
            totalQuestion = templateData?.numberOfQuestions ?: 0,
            lastUpdated = DateTimeUtil.getCurrentDateIsoUtc0(),
            status = InspectionStatus.IN_PROGRESS,
            conducted = conductedIso
        )
        return mapResultResourceAsFlow(
            action = {
                inspectionRepository.insertInspection(data)
            },
            transform = { data }
        )
    }

    private fun insertAnswers(
        inspectionId: String,
        templateData: InspectionTemplateModel?,
    ): Flow<FlowResult<String>> {
        val answerList = templateData?.pages?.flatMap { page ->
            page.sections.flatMap { section ->
                section.questions.map { question ->
                    AnswerModel(
                        id = Uuid.generate(),
                        inspectionId = inspectionId,
                        questionId = question.id,
                        lastUpdatedIsoUtc0 = DateTimeUtil.getCurrentDateIsoUtc0()
                    )
                }
            }
        }
        return mapResultResourceAsFlow(
            action = { answerRepository.insertAnswers(answerList ?: emptyList()) },
            transform = { inspectionId }
        )
    }

    operator fun invoke(
        templateId: String,
        inspectionId: String,
        conductedIso: String
    ): Flow<FlowResult<InspectionModel>> = flow { // return inspection id
        emit(FlowResult.Loading<InspectionModel>(showLoading = true))
        var inspectionTemplate: InspectionTemplateModel? = null
        templateRepository.getTemplateByIdFromLocalDb(templateId)
            .collect { result ->
                if (result is FlowResult.Success) {
                    inspectionTemplate = result.data
                }
            }

        if (inspectionTemplate == null) {
            emit(FlowResult.Loading<InspectionModel>(showLoading = false))
            return@flow
        }

        val inspectionResult = insertInspection(
            inspectionId = inspectionId,
            conductedIso = conductedIso,
            templateData = inspectionTemplate
        ).first { it is FlowResult.Success || it is FlowResult.Error }
        if (inspectionResult is FlowResult.Error) {
            emit(FlowResult.Error<InspectionModel>(inspectionResult.error))
            emit(FlowResult.Loading<InspectionModel>(showLoading = false))
            return@flow
        }

        val answerResult = insertAnswers(
            inspectionId = inspectionId,
            templateData = inspectionTemplate
        ).first { it is FlowResult.Success || it is FlowResult.Error }
        if (answerResult is FlowResult.Error) {
            emit(FlowResult.Error<InspectionModel>(answerResult.error))
            emit(FlowResult.Loading<InspectionModel>(showLoading = false))
            return@flow
        }

        emit(FlowResult.Loading<InspectionModel>(showLoading = false))
        emit(FlowResult.Success<InspectionModel>(inspectionResult.data))
    }.flowOn(Dispatchers.Default)
}
